if (!customElements.get('gipsform-gallery')) {
  customElements.define(
    'gipsform-gallery',
    class GipsformGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;

        this.slides = Array.from(this.querySelectorAll('[data-gf-slide]'));
        this.thumbnails = Array.from(this.querySelectorAll('[data-gf-thumb]'));
        this.counter = this.querySelector('[data-gf-current]');
        this.productLayout = this.closest('.product');
        this.mediaWrapper = this.closest('.product__media-wrapper');
        this.infoContainer = this.productLayout?.querySelector('.product__info-container');
        this.index = 0;
        this.touchStartX = null;
        this.syncFrame = null;

        this.querySelector('[data-gf-prev]')?.addEventListener('click', () => this.move(-1));
        this.querySelector('[data-gf-next]')?.addEventListener('click', () => this.move(1));

        this.thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => this.show(Number(thumbnail.dataset.index)));
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.move(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.move(1);
          }
        });

        this.addEventListener(
          'touchstart',
          (event) => {
            this.touchStartX = event.changedTouches[0]?.clientX ?? null;
          },
          { passive: true },
        );

        this.addEventListener(
          'touchend',
          (event) => {
            if (this.touchStartX === null) return;
            const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
            const distance = touchEndX - this.touchStartX;
            this.touchStartX = null;
            if (Math.abs(distance) < 45) return;
            this.move(distance > 0 ? -1 : 1);
          },
          { passive: true },
        );

        this.show(0, false);
        this.handleResize = () => this.scheduleHeightSync();
        window.addEventListener('resize', this.handleResize, { passive: true });

        if ('ResizeObserver' in window && this.infoContainer) {
          this.infoResizeObserver = new ResizeObserver(() => this.scheduleHeightSync());
          this.infoResizeObserver.observe(this.infoContainer);
        }

        this.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.handleResize, { once: true });
        });

        this.scheduleHeightSync();
        this.dataset.ready = 'true';
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        this.infoResizeObserver?.disconnect();
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        delete this.dataset.ready;
      }

      scheduleHeightSync() {
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncDesktopHeight();
        });
      }

      syncDesktopHeight() {
        if (!this.mediaWrapper || !this.infoContainer) return;

        if (!window.matchMedia('(min-width: 990px)').matches) {
          this.mediaWrapper.style.removeProperty('height');
          this.mediaWrapper.style.removeProperty('min-height');
          return;
        }

        const deliveryCard = this.infoContainer.querySelector('.gf-delivery-card');
        const visibleBottom = deliveryCard?.getBoundingClientRect().bottom
          ?? this.infoContainer.getBoundingClientRect().bottom;
        const galleryTop = this.mediaWrapper.getBoundingClientRect().top;
        const targetHeight = Math.ceil(visibleBottom - galleryTop);

        if (targetHeight > 0) {
          const value = `${targetHeight}px`;
          this.mediaWrapper.style.height = value;
          this.mediaWrapper.style.minHeight = value;
        }
      }

      move(direction) {
        if (this.slides.length < 2) return;
        const nextIndex = (this.index + direction + this.slides.length) % this.slides.length;
        this.show(nextIndex);
      }

      show(index, focusThumbnail = false) {
        if (!this.slides.length || index < 0 || index >= this.slides.length) return;
        this.index = index;

        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          slide.querySelectorAll('video').forEach((video) => {
            if (!active) video.pause();
          });
        });

        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const active = thumbnailIndex === index;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-selected', String(active));
        });

        const activeThumbnail = this.thumbnails[index];
        activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        if (focusThumbnail) activeThumbnail?.focus();
        if (this.counter) this.counter.textContent = String(index + 1);
      }
    },
  );
}
